import { FEDERAL_STATES } from '../../data/holidays-de';
import { PRODUCTS } from '../../data/products';
import { RECIPES } from '../../data/recipes';
import { STORES } from '../../data/stores';

// DE: Prüfungen für gespeicherte Daten: Nur Daten, die die Bildschirme sicher lesen können, gelten.
// EN: Checks for stored data: only data the screens can safely read is accepted.
type Check = (value: unknown) => boolean;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumber: Check = (value) => Number.isFinite(value);
const isText: Check = (value) => typeof value === 'string';
const isBoolean: Check = (value) => typeof value === 'boolean';
const listOf =
  (check: Check): Check =>
  (value) =>
    Array.isArray(value) && value.every(check);
const oneOf =
  (allowed: readonly unknown[]): Check =>
  (value) =>
    allowed.includes(value);
const shape =
  (fields: Readonly<Record<string, Check>>): Check =>
  (value) =>
    isRecord(value) && Object.entries(fields).every(([key, check]) => check(value[key]));

const recipeIds = () => RECIPES.map((recipe) => recipe.id);
const productIds = () => PRODUCTS.map((product) => product.id);
const storeIds = () => STORES.map((store) => store.id);

function memberShape(): Check {
  return shape({
    id: isText,
    name: isText,
    diet: oneOf(['omnivore', 'pescatarian', 'vegetarian', 'vegan']),
    excludedTags: listOf(isText),
    dislikedProductIds: listOf(isText),
    portionFactor: isNumber,
    meatAddOnsPerWeek: isNumber,
  });
}

/**
 * DE: Haushalt mit allen Feldern in der erwarteten Form und nur bekannten Märkten und Frühstücken.
 * EN: Household with every field in the expected form and only known stores and breakfasts.
 */
export function isHouseholdShape(value: unknown): boolean {
  const weekdays = listOf(oneOf([1, 2, 3, 4, 5, 6, 7]));
  const breakfastIds = listOf(oneOf(recipeIds()));
  const fields = shape({
    members: listOf(memberShape()),
    monthlyBudget: isNumber,
    wine: shape({ bottlesPerWeek: isNumber, maxBottlePrice: isNumber }),
    storeIds: listOf(oneOf(storeIds())),
    shoppingWeekdays: weekdays,
    workdays: weekdays,
    quick: shape({ activeMinutes: isNumber, totalMinutes: isNumber }),
    lowEnergyDates: listOf(isText),
    closedDates: listOf(isText),
    backupKitDinners: isNumber,
    fishDinnersPerWeek: isNumber,
    breakfasts: shape({ workday: breakfastIds, weekend: breakfastIds }),
  });
  const federalState = isRecord(value) ? value.federalState : undefined;
  return (
    fields(value) && (federalState === undefined || FEDERAL_STATES.includes(federalState as never))
  );
}

function dayShape(): Check {
  const recipe = oneOf(recipeIds());
  const addOn = shape({ memberId: isText, productId: oneOf(productIds()), quantity: isNumber });
  return shape({
    date: isText,
    breakfast: shape({ recipeId: recipe, servings: isNumber }),
    dinner: shape({
      recipeId: recipe,
      servings: isNumber,
      meatAddOns: listOf(addOn),
      locked: isBoolean,
    }),
  });
}

function tripShape(): Check {
  const item = shape({
    productId: oneOf(productIds()),
    storeId: oneOf(storeIds()),
    packages: isNumber,
    packagePrice: isNumber,
    regularPrice: isNumber,
    onOffer: isBoolean,
    estimated: isBoolean,
    total: isNumber,
  });
  return shape({
    id: isText,
    date: isText,
    items: listOf(item),
    total: isNumber,
    bought: isBoolean,
    checked: listOf(isText),
  });
}

/**
 * DE: Gespeicherter Plan mit Tagen und Einkäufen in der erwarteten Form und nur bekannten Kennungen.
 * EN: Stored plan with days and trips in the expected form and only known ids.
 */
export function isStoredPlanShape(value: unknown): boolean {
  const period = shape({ start: isText, days: isNumber });
  return shape({
    period,
    seed: isNumber,
    createdOn: isText,
    reserved: listOf(shape({ date: isText, recipeId: oneOf(recipeIds()) })),
    plan: shape({ period, days: listOf(dayShape()), warnings: listOf(isRecord) }),
    trips: listOf(tripShape()),
  })(value);
}

/**
 * DE: Vorrat: Produktkennung → Menge.
 * EN: Pantry: product id → quantity.
 */
export function isPantryShape(value: unknown): boolean {
  return isRecord(value) && Object.values(value).every(isNumber);
}

/**
 * DE: Liste von Einträgen (Angebote, eigene Preise, Ausgaben); im ersten Schritt nur als Liste geprüft.
 * EN: List of entries (offers, own prices, expenses); in the first slice only checked as a list.
 */
export const isEntryList: Check = listOf(isRecord);
